import { AccountType } from 'store/api/enums';
import { AdvancedUserRightsCodes } from 'store/app/enums';
import { User } from 'store/auth/models';
import {
  Entity,
  Organisation,
  OperationRight,
} from 'store/organisation/models';
import { MappedReturn } from 'store/returns/models/returnData';

// Define a union type for records that have operationRights
type RecordWithOperationRights = Entity | Organisation | MappedReturn;

/**
 * Checks if a user has a specific advanced user right for an entity/organisation
 * @param currentUser - The current user object
 * @param record - The entity or organisation record containing operation rights
 * @param requiredRight - The specific AdvancedUserRightsCodes to check for
 * @returns boolean indicating if the user has the required right
 */
export function hasAdvancedUserRight(
  currentUser: User | undefined,
  record: RecordWithOperationRights | undefined,
  requiredRight: AdvancedUserRightsCodes
): boolean {
  // If no user or no record, return false
  if (!currentUser || !record) {
    return false;
  }

  // If user is OWNER, they have all rights
  if (AccountType[currentUser.accountType] === AccountType.OWNER) {
    return true;
  }

  // For STAFF users, check specific advanced rights
  if (
    AccountType[currentUser.accountType] === AccountType.STAFF ||
    AccountType[currentUser.accountType] === AccountType.CLIENT
  ) {
    const hasRequiredRight = record.operationRights?.some(
      (right: OperationRight) =>
        right.operationRightCode === requiredRight && right.isActive
    );
    return !!hasRequiredRight;
  }

  // For all other account types (ADMIN, CLIENT, SUPPORT, etc.), no access
  return false;
}

// All specific helpers were removed in favor of the generic hasAdvancedUserRight
